import {
  Breakpoint,
  ButtonType,
  Color,
  PasswordForm,
  PasswordFormCondition,
  PasswordFormPayload,
  ShapeVariant,
  Typography,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useEffect, useId, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { AuthContext } from '../../contexts';
import { AuthReducerAction } from '../../enums';
import { useAuth, usePasswordConditions } from '../../hooks';
import { UseAuth } from '../../interfaces';
import { passwordRegexp } from '../../regexps';
import { AuthContextData } from '../../types/internal';

import {
  ContentWrapper,
  FormWrapper,
  ShapeContainer,
  StyledPopup,
  StyledShape,
  StyledTypography,
  TextWrapper,
} from './passwordUpdatePopup.styled';
import {
  PasswordUpdatePopupProps,
  UpdatePasswordApiError,
  UpdatePasswordData,
  UpdatePasswordError,
  UpdatePasswordMutation,
  UpdatePasswordResponse,
} from './passwordUpdatePopup.types';
import { updatePassword } from './services/api';

export const PasswordUpdatePopup: React.FC<PasswordUpdatePopupProps> = (props: PasswordUpdatePopupProps): JSX.Element => {
  const { isActive }: PasswordUpdatePopupProps = props;
  const { t }: TransProps<never> = useTranslation();
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.Tablet,
  });
  const [authContextState, dispatchAuthContext]: AuthContextData = useContext(AuthContext);
  const [isPopupOpen, setIsPopupOpen]: UseState<boolean> = useState(isActive);
  const [errorMessage, setErrorMessage]: UseState<UpdatePasswordError | undefined> = useState<UpdatePasswordError | undefined>();
  const passwordConditions: PasswordFormCondition[] = usePasswordConditions();
  const formId: string = useId();
  const { mutate: saveNewPassword, isPending }: UpdatePasswordMutation
  = useMutation<UpdatePasswordResponse, UpdatePasswordApiError, UpdatePasswordData>({
    mutationFn: ({ oldPassword, newPassword }: UpdatePasswordData): Promise<UpdatePasswordResponse> =>
      updatePassword(oldPassword, newPassword),
  });
  const { signOut }: UseAuth = useAuth();

  useEffect((): void => {
    setIsPopupOpen(isActive);
  }, [isActive]);

  const handlePopupClose: () => void = (): void => {
    signOut();
    setIsPopupOpen(false);
  };

  const onSubmit: (data: PasswordFormPayload) => void = (data: PasswordFormPayload): void => {
    if (!data.password) {
      return;
    }
    saveNewPassword(
      { oldPassword: data.password, newPassword: data.newPassword },
      {
        onSuccess: (): void => {
          if (!authContextState.appSettings?.passwordExpirationInfo) {
            return;
          }
          dispatchAuthContext({
            type: AuthReducerAction.UpdateAppSettings,
            payload: {
              passwordExpirationInfo: {
                ...authContextState.appSettings?.passwordExpirationInfo,
                forceChange: false,
              },
            },
          });
        },
        onError: (error: UpdatePasswordApiError): void => setErrorMessage(error.response?.data.errors),
      },
    );
  };

  return (
    <StyledPopup
      acceptButtonData={{
        formId,
        label: t('competence.app.componentsV2.passwordUpdatePopup.update'),
        isLoading: isPending,
        type: ButtonType.Submit,
      }}
      cancelButtonData={{
        label: t('competence.app.componentsV2.passwordUpdatePopup.logout'),
        onClick: handlePopupClose,
      }}
      isVisible={isPopupOpen}
      onClose={handlePopupClose}
      title={t('competence.app.componentsV2.passwordUpdatePopup.changePassword')}
    >
      <ContentWrapper>
        <TextWrapper>
          <StyledTypography variant={TypographyVariant.DisplayXS}>
            {t('competence.app.componentsV2.passwordUpdatePopup.passwordUpdateRequired')}
          </StyledTypography>
          <Typography variant={TypographyVariant.DisplayXS}>
            {t('competence.app.componentsV2.passwordUpdatePopup.updateYourPassword')}
          </Typography>
        </TextWrapper>
        <FormWrapper>
          <PasswordForm
            formId={formId}
            newPasswordError={errorMessage?.newPassword}
            oldPasswordError={errorMessage?.password}
            onSubmit={onSubmit}
            passwordConditions={passwordConditions}
            passwordRegexp={passwordRegexp}
            withOldPassword
          />
        </FormWrapper>
      </ContentWrapper>
      <ShapeContainer>
        <StyledShape color={isTablet ? Color.Brand300 : Color.Gray300} variant={ShapeVariant.Circle1} />
      </ShapeContainer>
    </StyledPopup>
  );
};
